import {
  Routes as BrowserRoutes,
  Navigate,
  Route,
  useLocation,
} from 'react-router-dom';
import { R } from 'config/app';
import { useEffect } from 'react';
import { useGoogleTagManager } from 'hooks/useGoogleTagManager';
import AffiliateScene from './scenes/AffiliateScene';
import ApprovedManualScene from './scenes/first-loan/ApprovedManualScene';
import ApprovedScene from './scenes/first-loan/ApprovedScene';
import CancelledScene from './scenes/first-loan/CancelledScene';
import FDFAppliedScene from './scenes/first-loan/FDFAppliedScene';
import IdentificationMethodScene from './scenes/first-loan/IdentificationMethodScene';
import InstructionsSentScene from './scenes/InstructionsSentScene';
import isProduction from 'utils/isProduction';
import LoginScene from './scenes/LoginScene';
import NewPasswordSetScene from './scenes/account/NewPasswordSetScene';
import NotFoundScene from './scenes/NotFoundScene';
import PasswordRecoveryScene from './scenes/PasswordRecoveryScene';
import PendingManualScene from './scenes/first-loan/PendingManualScene';
import QuestionnaireScene from './scenes/first-loan/QuestionnaireScene';
import RegisterScene from './scenes/first-loan/RegisterScene';
import RejectedScene from './scenes/first-loan/RejectedScene';
import RejectedTinkScene from './scenes/first-loan/RejectedTinkScene';
import RequiredAuth from './components/RequiredAuth';
import SetNewPasswordScene from './scenes/account/SetNewPasswordScene';
import TinkLoadingScene from './scenes/first-loan/TinkLoadingScene';
import TinkScene from './scenes/first-loan/TinkScene';
import TwoFAScene from './scenes/twoFAScene';
import useAuth from './hooks/useAuth';
import useClientQuery from './hooks/useClientQuery';
import useIdentificationTracker from './useIdentificationTracker';
import useLoanApplicationQuery from './hooks/useLoanApplicationQuery';
import usePageTracker from './usePageTracker';
import useResetAnalytics from './useResetAnalytics';
import useSmartlook from 'hooks/useSmartlook';
import WelcomeBackScene from './scenes/WelcomeBackScene';

const Routes = () => {
  const { isLoggedIn, fromTemporaryPassword, isForbidden, verificationId } =
    useAuth();
  const { pathname } = useLocation();
  const { data: clientData } = useClientQuery();
  const { loanApplication } = useLoanApplicationQuery();
  const { identifySmartlookClient } = useSmartlook();

  useResetAnalytics();
  useIdentificationTracker();
  usePageTracker();
  useGoogleTagManager();

  useEffect(() => {
    if (!isProduction() && clientData && loanApplication) {
      identifySmartlookClient(loanApplication, clientData);
    }
  }, [clientData, loanApplication]);

  if (verificationId && !isLoggedIn && pathname !== R.twoFA)
    return <Navigate to={R.twoFA} />;

  if (
    isLoggedIn &&
    fromTemporaryPassword &&
    pathname !== '/account/set-new-password'
  )
    return <Navigate to="/account/set-new-password" />;

  if (isForbidden && pathname !== R.FirstLoanCancelled)
    return <Navigate to={R.FirstLoanCancelled} />;

  return (
    <BrowserRoutes>
      <Route path="/">
        <Route index element={<Navigate to="/first-loan/register" />} />

        <Route
          path="callback"
          element={<Navigate to="/first-loan/register" />}
        />

        <Route path="login" element={<LoginScene />} />
        <Route path="/login/2fa" element={<TwoFAScene />} />

        <Route path="password-recovery">
          <Route index element={<PasswordRecoveryScene />} />
          <Route path="instructions-sent" element={<InstructionsSentScene />} />
        </Route>

        <Route path="account">
          <Route
            path="set-new-password"
            element={
              <RequiredAuth>
                <SetNewPasswordScene />
              </RequiredAuth>
            }
          />
          <Route path="new-password-set" element={<NewPasswordSetScene />} />
        </Route>

        <Route path="first-loan">
          <Route path="register">
            <Route index element={<RegisterScene />} />
          </Route>

          <Route path="questionnaire">
            <Route
              index
              element={
                <RequiredAuth>
                  <QuestionnaireScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="identification-method">
            <Route
              index
              element={
                <RequiredAuth>
                  <IdentificationMethodScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="tink">
            <Route
              index
              element={
                <RequiredAuth>
                  <TinkScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="tink-loading">
            <Route
              index
              element={
                <RequiredAuth>
                  <TinkLoadingScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="pending-manual">
            <Route
              index
              element={
                <RequiredAuth>
                  <PendingManualScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="approved-manual">
            <Route
              index
              element={
                <RequiredAuth>
                  <ApprovedManualScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="rejected">
            <Route
              index
              element={
                <RequiredAuth>
                  <RejectedScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="approved-tink">
            <Route
              index
              element={
                <RequiredAuth>
                  <ApprovedScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="rejected-tink">
            <Route
              index
              element={
                <RequiredAuth>
                  <RejectedTinkScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="fdf-applied">
            <Route
              index
              element={
                <RequiredAuth>
                  <FDFAppliedScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="cancelled">
            <Route
              index
              element={
                <RequiredAuth>
                  <CancelledScene />
                </RequiredAuth>
              }
            />
          </Route>

          <Route path="welcome-back">
            <Route
              index
              element={
                <RequiredAuth>
                  <WelcomeBackScene />
                </RequiredAuth>
              }
            />
          </Route>
        </Route>
        <Route path="/affiliate/:provider" element={<AffiliateScene />} />

        <Route path="*" element={<NotFoundScene />} />
      </Route>
    </BrowserRoutes>
  );
};

export default Routes;
